import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { campaignApi } from '../services/api';
import { Campaign } from '../types';

export default function OrderManagement() {
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<string>('');

  // Get campaigns with submitted or complete status for order management
  const { data: campaigns = [], isLoading, error } = useQuery({
    queryKey: ['campaigns', { status: statusFilter || 'submitted,complete' }],
    queryFn: () => campaignApi.getCampaigns({ 
      status: statusFilter || undefined 
    }),
  });

  const updateStatusMutation = useMutation({
    mutationFn: ({ campaignId, status }: { campaignId: string; status: string }) => 
      campaignApi.updateCampaignStatus(campaignId, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
    },
  });

  const handleStatusUpdate = (campaignId: string, status: string) => {
    if (confirm(`Are you sure you want to update the campaign status to "${status}"?`)) {
      updateStatusMutation.mutate({ campaignId, status });
    }
  };

  const formatDate = (dateString: string) => {
    return format(new Date(dateString), 'MMM dd, yyyy HH:mm');
  };

  const getStatusBadge = (status: Campaign['status']) => {
    const statusClass = `campaign-status status-${status}`;
    return <span className={statusClass}>{status.toUpperCase()}</span>;
  };

  // Filter campaigns to show only those with orders (submitted or complete)
  const orderedCampaigns = campaigns.filter(campaign => 
    campaign.status === 'submitted' || campaign.status === 'complete'
  );

  if (isLoading) {
    return <div className="loading">Loading orders...</div>;
  }

  if (error) {
    return (
      <div className="error">
        Error loading orders: {error instanceof Error ? error.message : 'Unknown error'}
      </div>
    );
  }

  return (
    <div>
      <div className="admin-header">
        <h1>Order Management</h1>
        <div>
          <span>{orderedCampaigns.length} orders</span>
        </div>
      </div>

      <div className="filters">
        <div className="filter-group">
          <label htmlFor="status-filter">Order Status</label>
          <select
            id="status-filter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">All Orders</option>
            <option value="submitted">Submitted</option>
            <option value="complete">Complete</option>
          </select>
        </div>
      </div>

      {orderedCampaigns.length === 0 ? (
        <div className="loading">
          {statusFilter ? 'No orders found with selected status' : 'No orders found'}
        </div>
      ) : (
        <div style={{ display: 'grid', gap: '1rem' }}>
          {orderedCampaigns.map((campaign) => (
            <div key={campaign.campaignId} style={{
              border: '1px solid #ddd',
              borderRadius: '8px',
              padding: '1.5rem',
              backgroundColor: 'white',
              display: 'grid',
              gridTemplateColumns: '1fr auto',
              gap: '2rem',
              alignItems: 'start'
            }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
                  <h3 style={{ margin: 0 }}>
                    <Link to={`/campaigns/${campaign.campaignId}`}>
                      {campaign.name}
                    </Link>
                  </h3>
                  {getStatusBadge(campaign.status)}
                </div>
                
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', fontSize: '0.875rem' }}>
                  <div>
                    <div style={{ fontWeight: 'bold', color: '#555' }}>Campaign Details</div>
                    <div>ID: {campaign.campaignId}</div>
                    <div>Card Size: {campaign.cardSize}</div>
                    <div>Paper: {campaign.paperStock}</div>
                    {campaign.recipientCount && (
                      <div>Recipients: {campaign.recipientCount}</div>
                    )}
                  </div>
                  
                  <div>
                    <div style={{ fontWeight: 'bold', color: '#555' }}>Timeline</div>
                    <div>Created: {formatDate(campaign.createdAt)}</div>
                    {campaign.updatedAt !== campaign.createdAt && (
                      <div>Updated: {formatDate(campaign.updatedAt)}</div>
                    )}
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', minWidth: '150px' }}>
                <Link 
                  to={`/campaigns/${campaign.campaignId}`}
                  className="btn btn-primary"
                >
                  View Details
                </Link>
                
                {campaign.status === 'submitted' && (
                  <button
                    onClick={() => handleStatusUpdate(campaign.campaignId, 'complete')}
                    className="btn btn-success"
                    disabled={updateStatusMutation.isPending}
                  >
                    {updateStatusMutation.isPending ? 'Updating...' : 'Mark Complete'}
                  </button>
                )}
                
                {campaign.status === 'complete' && (
                  <button
                    onClick={() => handleStatusUpdate(campaign.campaignId, 'submitted')}
                    className="btn btn-secondary"
                    disabled={updateStatusMutation.isPending}
                  >
                    {updateStatusMutation.isPending ? 'Updating...' : 'Mark Submitted'}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <div style={{ 
        marginTop: '2rem', 
        padding: '1rem', 
        backgroundColor: '#f8f9fa', 
        borderRadius: '8px',
        fontSize: '0.875rem',
        color: '#666'
      }}>
        <h4 style={{ margin: '0 0 0.5rem 0', color: '#333' }}>Order Management Guide</h4>
        <ul style={{ margin: 0, paddingLeft: '1.5rem' }}>
          <li><strong>Submitted:</strong> Order has been sent to the vendor and is being processed</li>
          <li><strong>Complete:</strong> Order has been fulfilled and cards have been mailed</li>
          <li>Use "View Details" to download export files and see recipient information</li>
          <li>Status updates help track fulfillment progress for internal reporting</li>
        </ul>
      </div>
    </div>
  );
}